import React, { useEffect, useState } from "react";
import Sidebar from "../../../layout/Sidebar";
import Header from "../../../layout/Header";
import Footer from "../../../layout/Footer";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { can } from "../../../utils/can";

const initialGotraForm = {
  religion_id: "",
  religion_name: "",
  caste_id: "",
  caste_name: "",
  sub_caste_id: "",
  sub_caste_name: "",
  name: "",
  status: "Active",
};

export default function SubCast() {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm();

  const [success, setSuccess] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [casts, setCasts] = useState([]);
  const [religions, setReligions] = useState([]);
  const [subcasts, setSubCast] = useState([]);
  const [editId, setEditId] = useState(null);
  const [pendingCasteId, setPendingCasteId] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [listLoading, setListLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [showAddGotraModal, setShowAddGotraModal] = useState(false);
  const [showViewGotraModal, setShowViewGotraModal] = useState(false);
  const [activeSubCaste, setActiveSubCaste] = useState(null);
  const [gotraForm, setGotraForm] = useState(initialGotraForm);
  const [gotraFormErrors, setGotraFormErrors] = useState({});
  const [gotraEditId, setGotraEditId] = useState(null);
  const [gotraLoading, setGotraLoading] = useState(false);
  const [gotraListLoading, setGotraListLoading] = useState(false);
  const [gotraList, setGotraList] = useState([]);
  const [gotraMessage, setGotraMessage] = useState("");
  const [gotraMessageType, setGotraMessageType] = useState("success");

  const token = localStorage.getItem("adminToken");
  const API_URL = process.env.REACT_APP_BASE_URL_API || "";
  const PAGE_SIZE = 10;
  const selectedReligion = watch("religion_id");

  const getSubCasteContext = (subcaste) => ({
    religion_id: subcaste?.caste_id?.religion_id?._id || "",
    religion_name: subcaste?.caste_id?.religion_id?.name || "",
    caste_id: subcaste?.caste_id?._id || "",
    caste_name: subcaste?.caste_id?.name || "",
    sub_caste_id: subcaste?._id || "",
    sub_caste_name: subcaste?.name || "",
  });

  const fetchReligion = async () => {
    try {
      const url = `${API_URL.replace(/\/$/, "")}/api/front/religion`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();

      if (response.ok) {
        setReligions(result.data || []);
      } else {
        setReligions([]);
        console.error(result.message || "Failed to fetch religions.");
      }
    } catch (err) {
      console.error("Error fetching religions:", err);
      setReligions([]);
    }
  };

  const fetchCasts = async (religionId, nextCasteId = null) => {
    if (!religionId) {
      setCasts([]);
      return;
    }

    try {
      const url = `${API_URL.replace(/\/$/, "")}/api/front/caste/${religionId}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json();
      const castList = result.data || [];
      setCasts(castList);

      if (nextCasteId && castList.some((c) => c._id === nextCasteId)) {
        setValue("caste_id", nextCasteId);
      }
      setPendingCasteId(null);
    } catch (err) {
      console.error("Error fetching caste:", err);
      setCasts([]);
      setPendingCasteId(null);
    }
  };

  const fetchSubCast = async (pageNumber = 1, currentSearch = activeSearch) => {
    setListLoading(true);
    try {
      let url = `${API_URL.replace(/\/$/, "")}/api/admin/sub-caste?page=${pageNumber}&limit=${PAGE_SIZE}`;
      if (currentSearch && currentSearch.trim()) {
        url += `&search=${encodeURIComponent(currentSearch.trim())}`;
      }
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();

      if (response.ok) {
        setSubCast(result.data || []);
        setTotalPages(result.totalPages || 1);
      } else {
        setSubCast([]);
        console.error(result.message || "Failed to fetch sub casts.");
      }
    } catch (err) {
      console.error("Error fetching sub casts:", err);
      setSubCast([]);
    } finally {
      setListLoading(false);
    }
  };

  const fetchGotraList = async (subCasteId) => {
    if (!subCasteId) {
      setGotraList([]);
      return;
    }

    setGotraListLoading(true);
    try {
      const url = `${API_URL.replace(/\/$/, "")}/api/admin/gotra?sub_caste_id=${subCasteId}&page=1&limit=100`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();

      if (response.ok) {
        setGotraList(result.data || []);
      } else {
        setGotraList([]);
      }
    } catch (err) {
      console.error("Error fetching gotra list:", err);
      setGotraList([]);
    } finally {
      setGotraListLoading(false);
    }
  };

  useEffect(() => {
    fetchReligion();
    fetchSubCast(page, activeSearch);
  }, [page, activeSearch]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    setActiveSearch(searchInput);
    fetchSubCast(1, searchInput);
  };

  const handleSearchReset = () => {
    setSearchInput("");
    setActiveSearch("");
    setPage(1);
    fetchSubCast(1, "");
  };

  useEffect(() => {
    if (!selectedReligion) {
      setCasts([]);
      return;
    }

    fetchCasts(selectedReligion, pendingCasteId);
  }, [selectedReligion, pendingCasteId]);

  const onSubmit = async (data) => {
    if (can("sub_caste", "create") === false) {
      alert("You don't have permission.");
      return;
    }

    setSuccess("");
    setErrorMsg("");
    setLoading(true);

    try {
      const url = editId
        ? `${API_URL.replace(/\/$/, "")}/api/admin/sub-caste/update/${editId}`
        : `${API_URL.replace(/\/$/, "")}/api/admin/sub-caste/create`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          religion_id: data.religion_id,
          caste_id: data.caste_id,
          name: data.name,
          status: data.isActive ? "Active" : "Deactive",
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setSuccess(editId ? "Sub Caste updated successfully!" : "Sub Caste added successfully!");
        setErrorMsg("");
        reset();
        setEditId(null);
        fetchSubCast(page, activeSearch);
      } else {
        setErrorMsg(result.message || "Failed to save sub caste.");
      }
    } catch (err) {
      console.error("Error:", err);
      setErrorMsg("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (subcaste) => {
    if (can("sub_caste", "edit") === false) {
      alert("You don't have permission.");
      return;
    }

    setValue("religion_id", subcaste?.caste_id?.religion_id?._id || "");
    setValue("caste_id", "");
    setValue("name", subcaste?.name || "");
    setValue("isActive", subcaste?.status === "Active");
    setEditId(subcaste?._id || null);
    setPendingCasteId(subcaste?.caste_id?._id || null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (can("sub_caste", "delete") === false) {
      alert("You don't have permission.");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this sub caste?")) {
      return;
    }

    try {
      const response = await fetch(`${API_URL.replace(/\/$/, "")}/api/admin/sub-caste/delete/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();

      if (response.ok) {
        setSuccess("Sub caste deleted successfully!");
        fetchSubCast(page);
      } else {
        setSuccess(result.message || "Failed to delete sub caste.");
      }
    } catch (err) {
      console.error(err);
      setSuccess("Something went wrong!");
    }
  };

  const closeAddGotraModal = () => {
    setShowAddGotraModal(false);
    setGotraEditId(null);
    setGotraForm(initialGotraForm);
    setGotraFormErrors({});
    setGotraMessage("");
    setGotraMessageType("success");
  };

  const closeViewGotraModal = () => {
    setShowViewGotraModal(false);
    setGotraMessage("");
    setGotraMessageType("success");
  };

  const openAddGotraModal = (subcaste) => {
    if (can("gotra", "create") === false) {
      alert("You don't have permission.");
      return;
    }

    const context = getSubCasteContext(subcaste);

    setActiveSubCaste(subcaste);
    setGotraEditId(null);
    setGotraForm({
      ...initialGotraForm,
      ...context,
      status: "Active",
    });
    setGotraFormErrors({});
    setGotraMessage("");
    setGotraMessageType("success");
    setShowViewGotraModal(false);
    setShowAddGotraModal(true);
  };

  const openViewGotraModal = async (subcaste) => {
    setActiveSubCaste(subcaste);
    setGotraMessage("");
    setGotraMessageType("success");
    setShowAddGotraModal(false);
    setShowViewGotraModal(true);
    await fetchGotraList(subcaste?._id);
  };

  const handleGotraInputChange = (event) => {
    const { name, value } = event.target;

    setGotraForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setGotraFormErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const validateGotraForm = () => {
    const validationErrors = {};

    if (!gotraForm.name.trim()) {
      validationErrors.name = "Please enter gotra name";
    } else if (gotraForm.name.trim().length < 2) {
      validationErrors.name = "Minimum 2 characters";
    }

    if (!gotraForm.status) {
      validationErrors.status = "Please select status";
    }

    setGotraFormErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleGotraSubmit = async (event) => {
    event.preventDefault();

    if (!validateGotraForm()) {
      return;
    }

    if (gotraEditId && can("gotra", "edit") === false) {
      alert("You don't have permission.");
      return;
    }

    const isEditMode = Boolean(gotraEditId);
    setGotraLoading(true);
    setGotraMessage("");
    setGotraMessageType("success");

    try {
      const url = isEditMode
        ? `${API_URL.replace(/\/$/, "")}/api/admin/gotra/update/${gotraEditId}`
        : `${API_URL.replace(/\/$/, "")}/api/admin/gotra/create`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          religion_id: gotraForm.religion_id,
          caste_id: gotraForm.caste_id,
          sub_caste_id: gotraForm.sub_caste_id,
          name: gotraForm.name.trim(),
          status: gotraForm.status,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setGotraMessage(isEditMode ? "Gotra updated successfully!" : "Gotra added successfully!");
        setGotraMessageType("success");
        await fetchGotraList(gotraForm.sub_caste_id);

        if (isEditMode) {
          setShowAddGotraModal(false);
          setShowViewGotraModal(true);
          setGotraEditId(null);
        }

        setGotraForm((prev) => ({
          ...prev,
          name: "",
          status: "Active",
        }));
        setGotraFormErrors({});
      } else {
        setGotraMessage(result.message || "Failed to save gotra.");
        setGotraMessageType("danger");
      }
    } catch (err) {
      console.error("Error saving gotra:", err);
      setGotraMessage("Something went wrong. Please try again.");
      setGotraMessageType("danger");
    } finally {
      setGotraLoading(false);
    }
  };

  const handleGotraEdit = (gotraItem) => {
    if (can("gotra", "edit") === false) {
      alert("You don't have permission.");
      return;
    }

    setGotraEditId(gotraItem?._id || null);
    setGotraForm({
      religion_id: gotraItem?.caste_id?.religion_id?._id || activeSubCaste?.caste_id?.religion_id?._id || "",
      religion_name: gotraItem?.caste_id?.religion_id?.name || activeSubCaste?.caste_id?.religion_id?.name || "",
      caste_id: gotraItem?.caste_id?._id || activeSubCaste?.caste_id?._id || "",
      caste_name: gotraItem?.caste_id?.name || activeSubCaste?.caste_id?.name || "",
      sub_caste_id: gotraItem?.sub_caste_id?._id || activeSubCaste?._id || "",
      sub_caste_name: gotraItem?.sub_caste_id?.name || activeSubCaste?.name || "",
      name: gotraItem?.name || "",
      status: gotraItem?.status || "Active",
    });
    setGotraFormErrors({});
    setGotraMessage("");
    setGotraMessageType("success");
    setShowViewGotraModal(false);
    setShowAddGotraModal(true);
  };

  const handleGotraDelete = async (gotraId) => {
    if (can("gotra", "delete") === false) {
      alert("You don't have permission.");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this gotra?")) {
      return;
    }

    try {
      const response = await fetch(`${API_URL.replace(/\/$/, "")}/api/admin/gotra/delete/${gotraId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();

      if (response.ok) {
        setGotraMessage("Gotra deleted successfully!");
        setGotraMessageType("success");
        await fetchGotraList(activeSubCaste?._id);
      } else {
        setGotraMessage(result.message || "Failed to delete gotra.");
        setGotraMessageType("danger");
      }
    } catch (err) {
      console.error("Error deleting gotra:", err);
      setGotraMessage("Something went wrong. Please try again.");
      setGotraMessageType("danger");
    }
  };

  return (
    <div className="container body">
      <div className="main_container">
        <Sidebar />
        <Header pageTitle="Sub Caste" />

        <div className="right_col" role="main">
          

          <div className="x_panel">
            <div className="x_title">
              <h2>Sub Caste Entry</h2>
              <ul className="nav navbar-right panel_toolbox" style={{ minWidth: 1 }}>
                <li>
                  <a className="collapse-link">
                    <i className="fa fa-chevron-up" />
                  </a>
                </li>
              </ul>
              <div className="clearfix" />
            </div>
            <div className="x_content">
              <div className="row">
                <div className="col-sm-12">
                  <div className="filtercont">
                    <form onSubmit={handleSubmit(onSubmit)}>
                      <div className="row">
                        <div className="col-12 col-sm-4 pr-lg-0 mb-3">
                          <label>Select Religion</label>
                          <select
                            className={`form-select ${errors.religion_id ? "is-invalid" : ""}`}
                            {...register("religion_id", { required: "Please select a religion" })}
                          >
                            <option value="">Select</option>
                            {religions.map((item) => (
                              <option key={item._id} value={item._id}>
                                {item.name}
                              </option>
                            ))}
                          </select>
                          {errors.religion_id && (
                            <div className="text-danger mt-1">{errors.religion_id.message}</div>
                          )}
                        </div>
                        <div className="col-12" />
                        <div className="col-12 col-sm-4 mb-3">
                          <label>Select Caste</label>
                          <select
                            className={`form-select ${errors.caste_id ? "is-invalid" : ""}`}
                            {...register("caste_id", { required: "Please select a cast" })}
                          >
                            <option value="">Select</option>
                            {casts.map((caste) => (
                              <option key={caste._id} value={caste._id}>
                                {caste.name}
                              </option>
                            ))}
                          </select>
                          {errors.caste_id && (
                            <div className="text-danger mt-1">{errors.caste_id.message}</div>
                          )}
                        </div>
                        <div className="col-12" />
                        <div className="col-12 col-sm-4 pr-lg-0 mb-3">
                          <label>Enter Sub Caste</label>
                          <input
                            type="text"
                            className={`form-control ${errors.name ? "is-invalid" : ""}`}
                            {...register("name", {
                              required: "Please enter sub cast name",
                              minLength: { value: 2, message: "Minimum 2 characters" },
                            })}
                          />
                          {errors.name && <div className="text-danger mt-1">{errors.name.message}</div>}
                        </div>
                        <div className="col-12" />
                        <div className="col-12 col-sm-4 pr-lg-0">
                          <input type="checkbox" id="active" {...register("isActive")} />
                          <label htmlFor="active" className="form-label">
                            Active/Deactive
                          </label>
                        </div>
                        <div className="col-12" />
                        <div className="col-12 col-sm-4 pr-lg-0 text-right">
                          <button type="submit" className="btn sitebtn" disabled={loading}>
                            {loading ? "Submitting..." : "Submit"}
                          </button>
                          {success && <div className="text-success mt-2">{success}</div>}
                          {errorMsg && <div className="text-danger mt-2">{errorMsg}</div>}
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="x_panel">
            <div className="x_title">
              <h2>Sub Caste List</h2>
              <ul className="nav navbar-right panel_toolbox" style={{ minWidth: 1 }}>
                <li>
                  <a className="collapse-link">
                    <i className="fa fa-chevron-up" />
                  </a>
                </li>
              </ul>
              <div className="clearfix" />
            </div>
            <div className="x_content">
              {/* Search Bar */}
              <div className="row mb-3">
                <div className="col-12 col-md-6 col-lg-4">
                  <form onSubmit={handleSearchSubmit}>
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search sub-caste..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                      />
                      <button className="btn sitebtn mb-0" type="submit" style={{ borderRadius: "0 4px 4px 0" }}>
                        <i className="fa fa-search" /> Search
                      </button>
                      {(activeSearch || searchInput) && (
                        <button
                          className="btn btn-secondary mb-0 ms-1 ml-1"
                          type="button"
                          onClick={handleSearchReset}
                        >
                          Reset
                        </button>
                      )}
                    </div>
                  </form>
                </div>
              </div>

              <div className="row">
                <div className="col-sm-12">
                  {listLoading ? (
                    <div>Loading...</div>
                  ) : (
                    <div className="table-responsive">
                      <table
                        id="datatable-checkbox"
                        className="table table-striped table-bordered bulk_action mytable"
                        style={{ width: "100%" }}
                        cellPadding={0}
                        cellSpacing={0}
                      >
                        <thead>
                          <tr>
                            <th style={{ width: 20 }}>S.No</th>
                            <th>Religion</th>
                            <th>Caste</th>
                            <th>Sub Caste</th>
                            <th style={{ minWidth: 70 }}>Gotra</th>
                            <th style={{ width: 70 }}>Status</th>
                            <th style={{ width: 100 }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {subcasts.length > 0 ? (
                            subcasts.map((item, index) => (
                              <tr key={item._id}>
                                <td>{(page - 1) * PAGE_SIZE + index + 1}</td>
                                <td>{item.caste_id?.religion_id?.name || "-"}</td>
                                <td>{item.caste_id?.name || "-"}</td>
                                <td>{item.name}</td>
                                <td>
                                  <button
                                    type="button"
                                    className="sitebtn text-white text-center px-1 py-0 mb-2"
                                    onClick={() => openAddGotraModal(item)}
                                    style={{ marginRight: 8, marginBottom: 6 }}
                                  >
                                    Add Gotra
                                  </button>
                                  <button
                                    type="button"
                                    className="sitebtn text-white text-center px-1 py-0 mb-2"
                                    onClick={() => openViewGotraModal(item)}
                                    style={{ marginBottom: 6 }}
                                  >
                                    <i className="fa fa-eye" /> View
                                  </button>
                                </td>
                                <td>
                                  <div className="checkbox-wrapper-8">
                                    <input
                                      className="tgl tgl-skewed"
                                      type="checkbox"
                                      checked={item.status === "Active"}
                                      readOnly
                                    />
                                    <label
                                      className="tgl-btn"
                                      data-tg-off="Inactive"
                                      data-tg-on="Active"
                                    />
                                  </div>
                                </td>
                                <td className="p-0">
                                  <ul className="actionlist">
                                    <li>
                                      <Link title="Edit" onClick={() => handleEdit(item)}>
                                        <i className="fa fa-edit" />
                                      </Link>
                                    </li>
                                    <li>
                                      <Link title="Delete" onClick={() => handleDelete(item._id)}>
                                        <i className="fa fa-trash" />
                                      </Link>
                                    </li>
                                  </ul>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={7} className="text-center">
                                No sub caste found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      <div className="mt-3">
                        {Array.from({ length: totalPages }, (_, i) => (
                          <button
                            key={i}
                            className={`btn btn-sm mx-1 ${page === i + 1 ? "btn-primary" : "btn-light"}`}
                            onClick={() => setPage(i + 1)}
                          >
                            {i + 1}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {showAddGotraModal && (
          <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h4 className="modal-title">{gotraEditId ? "Edit Gotra" : "Add Gotra"}</h4>
                  <button type="button" className="close" onClick={closeAddGotraModal}>
                    <span>&times;</span>
                  </button>
                </div>
                <form onSubmit={handleGotraSubmit}>
                  <div className="modal-body">
                    <div className="row">
                      <div className="col-sm-4 mb-3">
                        <label>Religion</label>
                        <input type="text" className="form-control" value={gotraForm.religion_name} readOnly />
                      </div>
                      <div className="col-sm-4 mb-3">
                        <label>Caste</label>
                        <input type="text" className="form-control" value={gotraForm.caste_name} readOnly />
                      </div>
                      <div className="col-sm-4 mb-3">
                        <label>Sub Caste</label>
                        <input type="text" className="form-control" value={gotraForm.sub_caste_name} readOnly />
                      </div>
                      <div className="col-sm-6 mb-3">
                        <label>Gotra</label>
                        <input
                          type="text"
                          name="name"
                          className={`form-control ${gotraFormErrors.name ? "is-invalid" : ""}`}
                          value={gotraForm.name}
                          onChange={handleGotraInputChange}
                        />
                        {gotraFormErrors.name && <div className="text-danger mt-1">{gotraFormErrors.name}</div>}
                      </div>
                      <div className="col-sm-6 mb-3">
                        <label>Status</label>
                        <select
                          name="status"
                          className={`form-select ${gotraFormErrors.status ? "is-invalid" : ""}`}
                          value={gotraForm.status}
                          onChange={handleGotraInputChange}
                        >
                          <option value="Active">Active</option>
                          <option value="Deactive">Deactive</option>
                        </select>
                        {gotraFormErrors.status && <div className="text-danger mt-1">{gotraFormErrors.status}</div>}
                      </div>
                    </div>
                    {gotraMessage && <div className={`text-${gotraMessageType}`}>{gotraMessage}</div>}
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-light btn-sm" onClick={closeAddGotraModal}>
                      Close
                    </button>
                    <button type="submit" className="btn btn sitebtn btn-sm" disabled={gotraLoading}>
                      {gotraLoading ? "Submitting..." : gotraEditId ? "Update" : "Submit"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {showViewGotraModal && (
          <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }}>
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h4 className="modal-title">Gotra List{activeSubCaste?.name ? ` - ${activeSubCaste.name}` : ""}</h4>
                  <button type="button" className="close" onClick={closeViewGotraModal}>
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <div className="row" style={{ marginBottom: 15 }}>
                    <div className="col-sm-4">
                      <strong>Religion:</strong> {activeSubCaste?.caste_id?.religion_id?.name || "-"}
                    </div>
                    <div className="col-sm-4">
                      <strong>Caste:</strong> {activeSubCaste?.caste_id?.name || "-"}
                    </div>
                    <div className="col-sm-4">
                      <strong>Sub Caste:</strong> {activeSubCaste?.name || "-"}
                    </div>
                  </div>

                  {gotraMessage && <div className={`text-${gotraMessageType} mb-3`}>{gotraMessage}</div>}

                  {gotraListLoading ? (
                    <div>Loading...</div>
                  ) : (
                    <div className="table-responsive" style={{ overflowY: "auto", maxHeight: "400px" }}>
                      <table className="table table-striped table-bordered mytable" style={{ width: "100%" }} cellPadding={0} cellSpacing={0}>
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Gotra</th>
                            <th>Status</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {gotraList.length > 0 ? (
                            gotraList.map((item, index) => (
                              <tr key={item._id}>
                                <td>{index + 1}</td>
                                <td>{item.name}</td>
                                <td>{item.status}</td>
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={() => handleGotraEdit(item)}
                                    style={{ marginRight: 8 }}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-danger btn-sm"
                                    onClick={() => handleGotraDelete(item._id)}
                                  >
                                    Delete
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={4} className="text-center">
                                No gotra found for this sub caste.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-default" onClick={closeViewGotraModal}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <Footer />
      </div>
    </div>
  );
}